import { useCallback, useRef, useState } from 'react'
import { AlertCircle, Eye, FileText, Loader2, Paperclip, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { FormAttachment } from '@/api/attachmentsApi'
import { ATTACHMENT_ACCEPTED_MIME } from '@/types/cgt'
import { triageFiles, type FileRejection } from '@/lib/attachmentField'
import { formatBytes } from './AttachmentPreview'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import { SYSTEM_LABEL_LAYOUT, type ResolvedLabelLayout } from '@/lib/fieldLayout'
import { FieldRow } from './FieldRow'

/** A picked-but-not-uploaded file, for builder preview mode. */
export interface PreviewListFile {
  id: string
  fileName: string
  sizeBytes: number
}

interface Props {
  submissionId: string
  fieldId: string
  label: string
  helpText?: string
  /** The stored files, in slot order. */
  files: FormAttachment[]
  required: boolean
  locked: boolean
  /** True while an upload or remove on this field is in flight. */
  busy: boolean
  /** A failed upload/remove, or the completion attempt's "required" message. */
  error?: string
  onUploadFiles: (files: File[]) => void
  onRemove: (attachmentId: string) => void
  /** Builder preview: fully interactive, but nothing is persisted. */
  preview?: boolean
  previewFiles?: PreviewListFile[]
  onPreviewFilesChange?: (files: PreviewListFile[]) => void
  /** Where the label sits (z8nrz7djyx); see lib/fieldLayout. Absent = the system default. */
  layout?: ResolvedLabelLayout
  stacked?: boolean
}

/**
 * The Attachment field type (z8nrz7dp7p): a labelled list of documents that ARE
 * the field's value, with no limit on how many.
 *
 * Not the per-field add-on (`AttachmentControl`), which is a single file of
 * evidence beside another value and draws as an icon. Here the files are the
 * content, so each is listed by name and size with its own remove, and the add
 * action takes several at once.
 *
 * A picked batch is triaged per file before anything is uploaded: a file of the
 * wrong type or size is refused with its reason, and the valid ones still
 * attach. The server re-checks both and stays the authority.
 */
export function AttachmentField({
  submissionId,
  fieldId,
  label,
  helpText,
  files,
  required,
  locked,
  busy,
  error,
  onUploadFiles,
  onRemove,
  preview = false,
  previewFiles = [],
  onPreviewFilesChange,
  layout = SYSTEM_LABEL_LAYOUT,
  stacked = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [rejections, setRejections] = useState<FileRejection[]>([])
  const [viewing, setViewing] = useState<FormAttachment | null>(null)
  // Stable on purpose: the preview dialog's history effect depends on it, and a
  // fresh function each render would push and pop a history entry every time.
  const onViewerOpenChange = useCallback((open: boolean) => {
    if (!open) setViewing(null)
  }, [])

  const listed: { id: string; fileName: string; sizeBytes: number; stored?: FormAttachment }[] = preview
    ? previewFiles
    : files.map(f => ({ id: f.id, fileName: f.fileName, sizeBytes: f.sizeBytes, stored: f }))
  const missing = required && listed.length === 0

  const pick = (picked: File[]) => {
    if (picked.length === 0) return
    const { accepted, rejected } = triageFiles(picked)
    setRejections(rejected)
    if (accepted.length === 0) return
    if (preview) {
      onPreviewFilesChange?.([
        ...previewFiles,
        ...accepted.map(f => ({ id: crypto.randomUUID(), fileName: f.name, sizeBytes: f.size })),
      ])
      return
    }
    onUploadFiles(accepted)
  }

  const remove = (id: string) => {
    setRejections([])
    if (preview) {
      onPreviewFilesChange?.(previewFiles.filter(f => f.id !== id))
      return
    }
    onRemove(id)
  }

  // The label points at Add files, the control that changes the value. A
  // locked field has none, so its label then names the list alone.
  return (
    <FieldRow
      label={label}
      helpText={helpText}
      required={required}
      layout={layout}
      stacked={stacked}
      tall
      controlClassName='flex min-w-0 flex-1 flex-col gap-1.5'
      testId={`attachment-field-${fieldId}`}
    >
      {({ id }) => (
        <>
          {listed.length > 0 && (
            <ul aria-label={`${label} files`} className='divide-y rounded-lg border'>
              {listed.map(f => (
                <li key={f.id} className='flex items-center gap-2 px-3 py-1.5 text-sm'>
                  <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='min-w-0 flex-1 truncate' title={f.fileName}>
                    {f.fileName}
                  </span>
                  <span className='shrink-0 text-xs text-muted-foreground'>{formatBytes(f.sizeBytes)}</span>
                  {f.stored && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-7 px-2'
                      aria-label={`View ${f.fileName}`}
                      onClick={() => setViewing(f.stored!)}
                    >
                      <Eye className='h-3.5 w-3.5' />
                    </Button>
                  )}
                  {!locked && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      disabled={busy}
                      className='h-7 px-2 text-destructive hover:text-destructive'
                      aria-label={`Remove ${f.fileName}`}
                      onClick={() => remove(f.id)}
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className='flex flex-wrap items-center gap-2'>
            {!locked && (
              <Button
                id={id}
                type='button'
                variant='outline'
                size='sm'
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className={cn('h-8', missing && 'border-destructive/60 text-destructive')}
              >
                {busy ? (
                  <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Paperclip className='mr-1.5 h-3.5 w-3.5' />
                )}
                Add files
              </Button>
            )}
            <span className='text-xs text-muted-foreground'>
              {listed.length === 0 ? 'No files yet' : `${listed.length} file${listed.length === 1 ? '' : 's'}`}
            </span>
            {locked && listed.length === 0 && (
              <span className='text-xs text-muted-foreground'>No files attached.</span>
            )}
          </div>

          {(rejections.length > 0 || error) && (
            <div role='alert' className='space-y-0.5 text-xs text-destructive'>
              {rejections.map((r, i) => (
                <p key={`${r.fileName}-${i}`} className='flex items-start gap-1'>
                  <AlertCircle className='mt-0.5 h-3 w-3 shrink-0' />
                  <span>
                    <span className='font-medium'>{r.fileName}</span> was not attached. {r.reason}
                  </span>
                </p>
              ))}
              {error && (
                <p className='flex items-start gap-1'>
                  <AlertCircle className='mt-0.5 h-3 w-3 shrink-0' />
                  <span>{error}</span>
                </p>
              )}
            </div>
          )}

          <input
            ref={inputRef}
            type='file'
            multiple
            accept={ATTACHMENT_ACCEPTED_MIME.join(',')}
            className='hidden'
            data-testid={`attachment-field-input-${fieldId}`}
            onChange={e => {
              pick(Array.from(e.target.files ?? []))
              // Cleared so picking the same file again still fires a change event.
              e.target.value = ''
            }}
          />

          {viewing && (
            <AttachmentPreviewDialog
              submissionId={submissionId}
              fieldId={fieldId}
              attachmentId={viewing.id}
              fileName={viewing.fileName}
              sizeBytes={viewing.sizeBytes}
              open
              onOpenChange={onViewerOpenChange}
            />
          )}
        </>
      )}
    </FieldRow>
  )
}
